class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.currentOpenPanel = null;
    this.closeTimeout = null;
    this.overlay = null;
  }

  connectedCallback() {
    this.nav = this.querySelector('[data-mega-nav]');
    if (!this.nav) return;

    this.triggers = this.querySelectorAll('[data-mega-trigger]');
    this.panels = document.querySelectorAll('[data-mega-panel]');

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', this.handleTriggerEnter.bind(this));
      trigger.addEventListener('mouseleave', this.scheduleClose.bind(this));
      trigger.addEventListener('click', this.handleClick.bind(this));
    });

    this.panels.forEach((panel) => {
      panel.addEventListener('mouseenter', this.cancelClose.bind(this));
      panel.addEventListener('mouseleave', this.scheduleClose.bind(this));
    });

    document.addEventListener('click', this.handleOutsideClick.bind(this));
    document.addEventListener('keydown', this.handleEscape.bind(this));

    this.initCategoryTabs();

    this.boundHandleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.boundHandleScroll, {
      passive: true,
    });

    this.ensureOverlay();
    this.mountPanelsToHeader();
  }

  handleTriggerEnter(event) {
    this.cancelClose();
    const trigger = event.currentTarget;
    const panelId = trigger.getAttribute('aria-controls');

    if (!panelId) return;

    this.openPanel(panelId, trigger);
  }

  scheduleClose() {
    this.closeTimeout = setTimeout(() => {
      this.closeAllPanels();
    }, 300);
  }

  cancelClose() {
    clearTimeout(this.closeTimeout);
  }

  handleClick(event) {
    const trigger = event.currentTarget;
    const panelId = trigger.getAttribute('aria-controls');

    if (!panelId) return;

    const panel = document.getElementById(panelId);
    if (!panel) return;

    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.closeAllPanels();
    } else {
      event.preventDefault();
      this.openPanel(panelId, trigger);
    }
  }

  handleEscape(event) {
    if (event.key === 'Escape' && this.currentOpenPanel) {
      const triggerToFocus = this.querySelector('[aria-expanded="true"]');
      this.closeAllPanels();
      if (triggerToFocus) {
        triggerToFocus.focus();
      }
    }
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target)) {
      this.closeAllPanels();
    }
  }

  openPanel(panelId, trigger) {
    this.closeAllPanels();

    const panel = document.getElementById(panelId);
    if (!panel) return;

    this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));

    trigger.setAttribute('aria-expanded', 'true');
    panel.classList.add('mega-menu-desktop__panel--visible');
    this.showOverlay();

    this.currentOpenPanel = panel;
  }

  closeAllPanels() {
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    this.panels.forEach((panel) => {
      panel.classList.remove('mega-menu-desktop__panel--visible');
    });

    this.currentOpenPanel = null;
    this.hideOverlay();
  }

  initCategoryTabs() {
    const navs = this.querySelectorAll('[data-category-nav]');
    navs.forEach((nav) => {
      const triggers = nav.querySelectorAll('[data-category-trigger]');
      triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => this.activateCategoryPane(trigger));
        trigger.addEventListener('mouseenter', () => this.activateCategoryPane(trigger));
        if (index === 0) this.activateCategoryPane(trigger);
      });
    });
  }

  activateCategoryPane(trigger) {
    const paneId = trigger.getAttribute('aria-controls');
    const panel = trigger.closest('[data-mega-panel]');
    if (!paneId || !panel) return;
    const panes = panel.querySelectorAll('[data-category-pane]');
    const triggers = panel.querySelectorAll('[data-category-trigger]');
    panes.forEach((p) => p.classList.remove('mega-menu-desktop__category-pane--active'));
    triggers.forEach((t) => t.setAttribute('aria-selected', 'false'));
    const pane = panel.querySelector(`#${paneId}`);
    if (pane) pane.classList.add('mega-menu-desktop__category-pane--active');
    trigger.setAttribute('aria-selected', 'true');
  }

  handleScroll() {
    if (!this.currentOpenPanel) return;
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    this.scrollRaf = requestAnimationFrame(() => {
      this.closeAllPanels();
    });
  }

  disconnectedCallback() {
    if (this.boundHandleScroll) window.removeEventListener('scroll', this.boundHandleScroll);

    if (this.overlay && this.overlay.parentNode) this.overlay.parentNode.removeChild(this.overlay);
  }

  ensureOverlay() {
    if (this.overlay) return;
    const el = document.createElement('div');
    el.className = 'mega-menu-desktop__overlay';
    el.addEventListener('click', () => this.closeAllPanels());
    const host = document.querySelector('.shopify-section-header') || document.body;
    host.appendChild(el);
    this.overlay = el;
  }

  showOverlay() {
    this.ensureOverlay();
    this.overlay.classList.add('mega-menu-desktop__overlay--visible');
  }

  hideOverlay() {
    if (!this.overlay) return;
    this.overlay.classList.remove('mega-menu-desktop__overlay--visible');
  }

  mountPanelsToHeader() {
    const host = document.querySelector('.shopify-section-header');
    if (!host) return;
    document.querySelectorAll('[data-mega-panel]').forEach((panel) => {
      if (panel.parentNode !== host) host.appendChild(panel);
    });
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}
